// @ts-strict-ignore
import produce from 'immer';
import { createModel } from '@rematch/core';
import { mergeClients as apiMergeClients } from '../../api/Providers/MergeClients';
import {
  updateClient as apiUpdateClient,
  getClient,
  deleteClient,
  createMultipleClients as apiCreateMultipleClients,
  blockClient,
  unblockClient,
} from '../../api/Providers/Clients';
import type { IProviderClient } from '../../api/Providers/Clients/types';
import type { RootModel } from '../models';
import { PartialProviderClient } from '../../modules/provider/Client';
import {
  canSendClientApptOptInSms,
  ClientSMSStatus,
  getStatus as getSMSStatus,
} from '../../modules/api/clients/sms';
import EventEmitter from '../../modules/EventEmitter';
import { ReminderType } from '../../api/Providers/Appointments';

export type ProviderClientsState = {
  providerId: number | null;
  loading: boolean;
  clients: Record<number, IProviderClient>;
  clientSmsInviteStatus: Record<number, ClientSMSStatus>;
};

export enum ClientEvents {
  Merged = 'mergedClients',
  Blocked = 'blockedClient',
  Unblocked = 'unblockedClient',
  Created = 'createdClient',
  Deleted = 'deletedClient',
}

type ProviderAndClientId = {
  providerId: number | string;
  clientId: number;
};

type LoadClientPayload = ProviderAndClientId;

type RemoveClientPayload = ProviderAndClientId;

type BlockClientPayload = ProviderAndClientId;

type UnblockClientPayload = ProviderAndClientId;

type MergeClientPayload = {
  providerId: number | string;
  clientIdA: number;
  clientIdB: number;
};

type UpdateClientPayload = {
  providerId: number | string;
  client: PartialProviderClient & Pick<IProviderClient, 'id'>;
};

type AddMultipleClientsPayload = {
  providerId: number | string;
  clients: PartialProviderClient[];
};

type UpdateReminderPreferencePayload = {
  providerId: number | string;
  clientId: number;
  reminderPreference?: ReminderType;
};

type OnClientLoadedPayload = {
  providerId: number | string;
  client: IProviderClient;
};

type OnSmsStatusLoadedPayload = {
  providerId: number | string;
  clientId: number | string;
  status: ClientSMSStatus;
};

type OnClientsPageLoadedPayload = {
  providerId: number | string;
  clients: Array<IProviderClient>;
};

type OnClientBlockUpdatedPayload = {
  clientId: number;
  blocked: boolean;
};

function getInitialState(): ProviderClientsState {
  return {
    loading: false,
    providerId: null,
    clients: {},
    clientSmsInviteStatus: {},
  };
}

export const providerClients = createModel<RootModel>()({
  name: 'providerClients',

  state: getInitialState(),

  reducers: {
    'user/logout': () => getInitialState(),

    onClientLoading: (state: ProviderClientsState): ProviderClientsState => ({
      ...state,
      loading: true,
    }),

    onClientLoaded: produce<ProviderClientsState, [OnClientLoadedPayload]>((
      state: ProviderClientsState,
      payload: OnClientLoadedPayload,
    ) => {
      const providerId = Number(payload.providerId);

      if (providerId === state.providerId) {
        state.clients[payload.client.id] = payload.client;
      } else {
        state.providerId = providerId;
        state.clients = { [payload.client.id]: payload.client };
      }

      state.loading = false;
    }),

    onSMSStatusLoaded: produce<ProviderClientsState, [OnSmsStatusLoadedPayload]>((
      state: ProviderClientsState,
      payload: OnSmsStatusLoadedPayload,
    ) => {
      const providerId = Number(payload.providerId);

      if (providerId === state.providerId) {
        state.clientSmsInviteStatus[payload.clientId] = payload.status;
      } else {
        state.providerId = providerId;
        state.clientSmsInviteStatus = { [Number(payload.clientId)]: payload.status };
      }

      state.loading = false;
    }),

    onClientsPageLoaded: (
      state: ProviderClientsState,
      payload: OnClientsPageLoadedPayload,
    ): ProviderClientsState => {
      const providerId = Number(payload.providerId);
      let clientMap: Record<number, IProviderClient> = {};

      const newClientsMap = payload.clients.reduce((result, client) => ({
        ...result,
        [client.id]: client,
      }), {});

      if (providerId === state.providerId) {
        clientMap = {
          ...clientMap, ...(state.clients || {}), ...newClientsMap,
        };
      } else {
        clientMap = newClientsMap;
      }

      return {
        ...state,
        providerId,
        clients: clientMap,
      };
    },

    onClientBlockUpdated: produce<ProviderClientsState, [OnClientBlockUpdatedPayload]>((
      state: ProviderClientsState,
      payload: OnClientBlockUpdatedPayload,
    ) => {
      state.clients[payload.clientId].blocked = payload.blocked;
    }),

    onClientRemoved: produce<ProviderClientsState, [RemoveClientPayload]>((
      state: ProviderClientsState,
      payload: RemoveClientPayload,
    ) => {
      delete state.clients[payload.clientId];
    }),
  },

  effects: dispatch => ({
    async loadClient({ providerId, clientId }: LoadClientPayload) {
      dispatch.providerClients.onClientLoading();

      let client: IProviderClient;

      try {
        client = await getClient(providerId, clientId);

        dispatch.providerClients.onClientLoaded({
          providerId,
          client,
        });
      } catch (err) {
        if (err?.code === 404) {
          // If the response code is 404, remove this client and continue with the error pipeline
          dispatch.providerClients.onClientRemoved({ providerId, clientId });
        }

        throw err;
      }

      return client;
    },

    async loadClientSMSStatus(
      { providerId, clientId }: LoadClientPayload,
      rootState,
    ): Promise<ClientSMSStatus> {
      let client: IProviderClient = rootState.providerClients.clients[clientId];

      if (!client) {
        client = await getClient(providerId, clientId);
        dispatch.providerClients.onClientLoaded({ providerId, client });
      }

      dispatch.providerClients.onClientLoading();

      const status = getSMSStatus(await canSendClientApptOptInSms(clientId));

      dispatch.providerClients.onSMSStatusLoaded({
        providerId,
        clientId,
        status,
      });

      return status;
    },

    async updateReminderPreference(
      {
        clientId,
        reminderPreference,
        providerId,
      }: UpdateReminderPreferencePayload,
      rootState,
    ): Promise<void> {
      const newPreference: ReminderType | null = reminderPreference || null;
      const oldPreference = rootState.providerClients.clients[clientId].reminder_preference;
      if (oldPreference !== undefined && (oldPreference !== newPreference)) {
        await dispatch.providerClients.updateClient({
          providerId,
          client: {
            id: clientId,
            reminder_preference: newPreference,
          },
        });

        if (
          clientId
          && (
            reminderPreference === ReminderType.SmsEmailReminder
            || reminderPreference === ReminderType.SmsOnlyReminder
          )
        ) {
          dispatch.providerClients.loadClientSMSStatus({ providerId, clientId });
        }
      }
    },

    async removeClient({ providerId, clientId }: RemoveClientPayload) {
      await deleteClient(Number(providerId), clientId);
      EventEmitter.emit(ClientEvents.Deleted, clientId);

      dispatch.providerClients.onClientRemoved({ providerId, clientId });
    },

    /**
     * Takes two client IDs and merges them, yielding the ID of the resulting client
     */
    async mergeClients({
      providerId, clientIdA, clientIdB,
    }: MergeClientPayload): Promise<number> {
      const result = await apiMergeClients(Number(providerId), clientIdA, clientIdB);

      await Promise.all([
        dispatch.providerClients.onClientRemoved({ providerId, clientId: clientIdA }),
        dispatch.providerClients.onClientRemoved({ providerId, clientId: clientIdB }),
        dispatch.providerClients.loadClient({ providerId, clientId: result.new_client }),
      ]);
      EventEmitter.emit(ClientEvents.Merged);

      return result.new_client;
    },

    async updateClient({ providerId, client }: UpdateClientPayload): Promise<IProviderClient> {
      const clientPayload = { phones: [], ...client };

      const result = await apiUpdateClient(providerId, clientPayload);

      dispatch.providerClients.onClientLoaded({ providerId, client: result });

      return result;
    },

    async blockClient({ providerId, clientId }: BlockClientPayload): Promise<void> {
      await blockClient(providerId, clientId);
      EventEmitter.emit(ClientEvents.Blocked, clientId);

      dispatch.providerClients.onClientBlockUpdated({ clientId, blocked: true });
    },

    async unblockClient({ providerId, clientId }: UnblockClientPayload): Promise<void> {
      await unblockClient(providerId, clientId);
      EventEmitter.emit(ClientEvents.Unblocked, clientId);

      dispatch.providerClients.onClientBlockUpdated({ clientId, blocked: false });
    },

    async createMultipleClients(
      { providerId, clients }: AddMultipleClientsPayload,
    ): Promise<Array<IProviderClient>> {
      const resultClients = await apiCreateMultipleClients(
        Number(providerId),
        clients,
      );
      EventEmitter.emit(ClientEvents.Created);

      dispatch.providerClients.onClientsPageLoaded({
        clients: resultClients,
        providerId,
      });

      return resultClients;
    },
  }),

  selectors: (slice, createSelector, hasProps) => ({
    client: hasProps((_, clientId: number) => (
      createSelector(
        slice,
        (state: ProviderClientsState) => state.clients[clientId],
      )
    )),

    clientSMSStatus: hasProps((_, clientId: number | undefined) => (
      createSelector(
        slice,
        (state: ProviderClientsState): ClientSMSStatus => (
          clientId ? (state.clientSmsInviteStatus[clientId]
            || ClientSMSStatus.NoSmsPermissionHistory) : ClientSMSStatus.NoSmsPermissionHistory
        ),
      )
    )),

    clientDeleted: hasProps((_, clientId: number) => (
      createSelector(
        slice,
        (state: ProviderClientsState): boolean => !state.loading && !state.clients[clientId],
      )
    )),
  }),
});
